/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  HrBlockContainer,
  HrGridContainer,
  HrFlexContainer,
  HrTitleContainer,
  HrTitleH4,
  HrTitleH5,
  HrTitleH6,
  HrSecondary,
  HrActionText,
  HrPrimary,
  HrParagraph,
  HrRed
} from '@components/styles/hr-styles';
import { Input } from '@components/form/input';
import { SelectForm } from '@components/form/select';
import { Textarea } from '@components/form/textarea';
import styled from 'styled-components';
import { RadioForm } from '@components/form/radio/radio';
import { ErrorMessage } from '@hookform/error-message';
import { _timeAvailability } from '../../../constant/vacancy';
import { vacancyRequirementType } from '../../../constant/vacancy-requirement';
import useStore from '../../store/useStore';
import { DisplaySettingsContext } from '../../store/display-settings';

const GridContainer = styled(HrGridContainer)`
  padding: 0 32px;

  @media (max-width: 768px) {
    padding: 0 16px;
    grid-template-columns: 1fr;
  }
`;

const BlockContainer = styled(HrBlockContainer)`
  padding: 0 32px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Addbutton = styled(HrActionText)`
  font-size: ${(props) => `${16 + (props.fontSizeSetting || 0)}px`};
  color: ${HrPrimary};
  margin-left: 32px !important;
  @media (max-width: 768px) {
    margin-left: 16px !important;
  }
`;

export const PersonalExpectations = observer(
  ({ setValue, vacancyRequirement, register, formState, control, postulant }) => {
    const { getDataPostulant, vacancy } = useStore();
    const [t] = useTranslation();
    const [show, setShow] = useState(false);
    const { additional_message } = postulant;
    const { currentAccountMoney } = getDataPostulant;

    useEffect(() => {
      if (additional_message) {
        setShow(true);
      }
    }, [additional_message]);

    const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);
    const salaryTypeText = vacancy?.req_salaryType ? `en ${vacancy.req_salaryType}` : 'en bruto';

    return (
      <HrFlexContainer flexDirection='column' alignItems='flex-start'>
        <HrTitleContainer borderBottom={darkMode ? '1px solid #767676' : ''}>
          <HrTitleH4 fontSizeSetting={fontSizeSetting} color={darkMode ? 'white' : ''}>
            {t('postulant.personal-expectations.title')}
          </HrTitleH4>
        </HrTitleContainer>
        {vacancyRequirement.relocation !== vacancyRequirementType.disabled && (
          <BlockContainer>
            <HrTitleH5
              fontSizeSetting={fontSizeSetting}
              color={darkMode ? 'white' : ''}
              isRequired={vacancyRequirement.relocation === vacancyRequirementType.mandatory}
            >
              {t('postulant.personal-expectations.relocation')}
            </HrTitleH5>
            <HrFlexContainer padding='16px 0 0 0'>
              <RadioForm
                name='relocation'
                text={t('postulant.education.high_school_data.yes')}
                value='true'
                control={control}
                validate={{ required: vacancyRequirement.relocation === vacancyRequirementType.mandatory }}
                key='relocation1'
                formState={formState}
                register={register}
                setValue={setValue}
                checked={postulant.relocation === 'true' ? true : null}
              />
              <RadioForm
                name='relocation'
                text={t('postulant.education.high_school_data.no')}
                value={false}
                control={control}
                validate={{ required: vacancyRequirement.relocation === vacancyRequirementType.mandatory }}
                key='relocation2'
                register={register}
                setValue={setValue}
                checked={postulant.relocation === 'false' ? true : null}
              />
              <ErrorMessage
                errors={formState.errors}
                name='relocation'
                render={({ message }) => <HrParagraph color={HrRed}>{message}</HrParagraph>}
              />
            </HrFlexContainer>
          </BlockContainer>
        )}
        <GridContainer>
          {vacancyRequirement.remuneration !== vacancyRequirementType.disabled && (
            <HrFlexContainer flexDirection='column' gap='8px' alignItems='flex-end'>
              <Input
                title={`${t('postulant.personal-expectations.remuneration.desiredSalary')} ${salaryTypeText}`}
                placeholder={t(`crudVacancy.wizardForm.step1.placeholder.remuneration`)}
                name='desiredSalary'
                type='number'
                register={register}
                formState={formState}
                symbol={vacancy && `${vacancy.currencySymbol} (${vacancy.currency})`}
                validate={{
                  required: vacancyRequirement.remuneration === vacancyRequirementType.mandatory,
                  maxLength: 15,
                  min: 0,
                  whiteSpace: true
                }}
              />
            </HrFlexContainer>
          )}

          {vacancyRequirement.available_time !== vacancyRequirementType.disabled && (
            <SelectForm
              title={t('postulant.personal-expectations.available_time')}
              id='available_time'
              data={_timeAvailability}
              control={control}
              formState={formState}
              isSearchable
              validate={{ required: vacancyRequirement.available_time === vacancyRequirementType.mandatory }}
              // defaultValue={vacancy.razonBusqueda}
            />
          )}
        </GridContainer>
        {show && (
          <BlockContainer>
            <Textarea
              title={t('postulant.personal-expectations.additional_message')}
              id='additional_message'
              control={control}
              placeholder={t('postulant.personal-expectations.additional_message_placeholder')}
            />
          </BlockContainer>
        )}
        {!show && (
          <Addbutton
            fontSizeSetting={fontSizeSetting}
            onClick={() => {
              setShow(true);
            }}
          >
            {t('postulant.personal-expectations.add')}
          </Addbutton>
        )}
      </HrFlexContainer>
    );
  }
);
